import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Truck, CheckCircle2, MapPin, Phone, HelpCircle, Clock, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatPrice = (p) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(p);

const getColombiaToday = () => {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Bogota',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(new Date());
};

export default function AdminDeliveries() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeRegion, setActiveRegion] = useState('Todos');
  const [selectedDate, setSelectedDate] = useState(() => {
    return getColombiaToday();
  });

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/orders`, { withCredentials: true, headers: getHeaders(), params: { status: 'delivery' } });
      setOrders(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const [paymentAmount, setPaymentAmount] = useState({});
  const [paymentMethod, setPaymentMethod] = useState({});
  const [paymentDate, setPaymentDate] = useState({});
  const [showPaymentForm, setShowPaymentForm] = useState({});

  const fetchProducts = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/products`, { withCredentials: true, headers: getHeaders() });
      setProducts(data);
    } catch {}
  };

  useEffect(() => {
    fetchOrders();
    fetchProducts();
  }, []);

  const markDelivered = async (orderId) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status`, { status: 'delivered' }, { withCredentials: true, headers: { ...getHeaders(), 'Content-Type': 'application/json' } });
      toast.success('Marcado como entregado');
      fetchOrders();
    } catch (err) { 
      const errMsg = err.response?.data?.detail || err.message || 'Error desconocido';
      toast.error(`Error: ${errMsg}`); 
    }
  };

  const markOnTheWay = async (orderId) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status`, { status: 'on_the_way' }, { withCredentials: true, headers: { ...getHeaders(), 'Content-Type': 'application/json' } });
      toast.success('Marcado en camino');
      fetchOrders();
      const order = orders.find(o => o.id === orderId);
      if (order && order.customer_phone) {
        const cleanPhone = order.customer_phone.replace(/\D/g, '');
        const phoneWithCountry = cleanPhone.length === 10 ? `57${cleanPhone}` : cleanPhone;
        const msg = encodeURIComponent(`Hola ${order.customer_name}, tu pedido de Keiks Repostería con código ${order.tracking_code} ya está en camino a tu domicilio! 🛵`);
        window.open(`https://wa.me/${phoneWithCountry}?text=${msg}`, '_blank');
      }
    } catch (err) { 
      const errMsg = err.response?.data?.detail || err.message || 'Error desconocido';
      toast.error(`Error: ${errMsg}`); 
    }
  };

  const handleAddPayment = async (orderId) => {
    const order = orders.find(o => o.id === orderId);
    const defaultAmount = Math.max(0, (order?.total || 0) - (order?.amount_paid || 0));
    const amount = paymentAmount[orderId] !== undefined ? paymentAmount[orderId] : defaultAmount;
    const method = paymentMethod[orderId] || 'cash';
    const date = paymentDate[orderId] || new Date().toISOString().split('T')[0];
    if (amount <= 0) {
      toast.error('El monto debe ser mayor a 0');
      return;
    }
    try {
      await axios.post(`${API}/admin/orders/${orderId}/payments`, { amount, method, date }, { withCredentials: true, headers: { ...getHeaders(), 'Content-Type': 'application/json' } });
      toast.success('Pago registrado con éxito');
      setShowPaymentForm(prev => ({ ...prev, [orderId]: false }));
      setPaymentAmount(prev => {
        const next = { ...prev };
        delete next[orderId];
        return next;
      });
      setPaymentDate(prev => {
        const next = { ...prev };
        delete next[orderId];
        return next;
      });
      fetchOrders();
    } catch {
      toast.error('Error al registrar pago');
    }
  };

  const getOrderRegion = (order) => {
    const normalize = (str) => (str || '')
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, ""); // removes accents
      
    const store = normalize(order.store_name);
    const address = normalize(order.delivery_address);
    
    if (store.includes('bello') || address.includes('bello')) return 'Bello';
    if (store.includes('boston') || address.includes('boston')) return 'Boston';
    if (store.includes('envigado') || address.includes('envigado')) return 'Envigado';
    if (store.includes('bolivar') || address.includes('bolivar') || store.includes('centro') || address.includes('centro')) return 'Bolívar';
    if (store.includes('belen') || address.includes('belen')) return 'Belén';
    
    return 'Otros';
  };

  const getSlotWeight = (order) => {
    const timeStr = `${order.delivery_time_slot || ''} ${order.delivery_address || ''}`.toUpperCase();
    if (timeStr.includes('9:00 AM') || timeStr.includes('9 AM') || timeStr.includes('MAÑANA')) return 1;
    if (timeStr.includes('12:00 M')) return 2;
    if (timeStr.includes('2:00 PM') || timeStr.includes('2 PM')) return 3;
    if (timeStr.includes('3:00 PM') || timeStr.includes('3 PM')) return 4;
    if (timeStr.includes('6:00 PM') || timeStr.includes('TARDE')) return 5;
    return 6;
  };

  const filteredOrders = selectedDate
    ? orders.filter(o => o.delivery_date && o.delivery_date.startsWith(selectedDate))
    : orders;

  const sortedAndFiltered = [...filteredOrders]
    .sort((a, b) => getSlotWeight(a) - getSlotWeight(b))
    .filter(o => activeRegion === 'Todos' || getOrderRegion(o) === activeRegion);

  const deliveryOrders = sortedAndFiltered.filter(o => o.delivery_type === 'delivery');
  const pickupOrders = sortedAndFiltered.filter(o => o.delivery_type === 'pickup');

  const regions = ['Todos', 'Bello', 'Boston', 'Envigado', 'Bolívar', 'Belén', 'Otros'];

  const renderOrderCard = (order) => {
    const itemsTotal = order.items?.reduce((acc, item) => acc + (item.subtotal * (item.quantity || 1)), 0) || 0;
    const deliveryFee = order.delivery_type === 'delivery' ? (order.delivery_fee || 0) : 0;
    const computedTotal = itemsTotal + deliveryFee;
    const computedBalance = Math.max(0, computedTotal - (order.amount_paid || 0));
    const isPendingPayment = computedBalance > 0;
    return (
      <Card key={order.id} className={`p-5 border rounded-2xl flex flex-col justify-between ${order.status === 'on_the_way' ? 'border-orange-300 bg-orange-50/20' : 'border-gray-200 bg-white'}`} data-testid={`delivery-order-${order.tracking_code}`}>
        <div>
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="font-body text-sm font-semibold text-[#46371c]">{order.tracking_code}</p>
              <p className="font-body text-xs text-[#75664d]">{order.customer_name}</p>
            </div>
            {order.status === 'on_the_way' ? (
              <Badge className="bg-orange-100 text-orange-800 flex flex-col items-center">
                <span>En Camino 🛵</span>
                {order.courier_name && <span className="text-[9px] font-normal font-body mt-0.5">({order.courier_name})</span>}
              </Badge>
            ) : order.status === 'delivery' ? (
              <Badge className="bg-green-100 text-green-800">Listo ✅</Badge>
            ) : order.status === 'production' ? (
              <Badge className="bg-purple-100 text-purple-800">En Producción ⏳</Badge>
            ) : (
              <Badge className="bg-blue-100 text-blue-800">Por Iniciar 📋</Badge>
            )}
          </div>

          {order.delivery_date && (
            <p className="font-body text-xs text-[#75664d] flex items-center gap-1 mb-2">
              <Clock className="w-3.5 h-3.5 text-[#75664d]" />
              <span className="font-semibold text-[#46371c]">Fecha de Entrega:</span> {order.delivery_date.split('T')[0]} {order.delivery_time_slot && <Badge variant="outline" className="ml-2 bg-[#a0d5ba]/10 border-[#a0d5ba] text-[#46371c] font-semibold text-[10px]">{order.delivery_time_slot}</Badge>}
            </p>
          )}
          {order.delivery_address && (
            <p className="font-body text-xs text-[#75664d] flex items-center gap-1 mb-2">
              <MapPin className="w-3.5 h-3.5 text-[#75664d]" />
              <span className="font-semibold text-[#46371c]">Dirección:</span> {order.delivery_address}
            </p>
          )}
          {order.store_name && (
            <p className="font-body text-xs text-[#75664d] flex items-center gap-1 mb-2">
              <span className="font-semibold text-[#46371c]">Sede:</span> {order.store_name}
            </p>
          )}
          <p className="font-body text-xs text-[#75664d] flex items-center gap-1 mb-3">
            <Phone className="w-3.5 h-3.5 text-[#75664d]" />
            <span className="font-semibold text-[#46371c]">Teléfono:</span> {order.customer_phone}
            <span className="inline-flex gap-2 ml-2">
              <a href={`tel:${order.customer_phone}`} className="p-1 hover:bg-[#f4f1ea] rounded text-[#46371c]" title="Llamar">
                📞
              </a>
              <a href={`https://wa.me/${order.customer_phone.replace(/\+/g, '').replace(/\s/g, '')}`} target="_blank" rel="noopener noreferrer" className="p-1 hover:bg-[#f4f1ea] rounded text-green-600 font-semibold" title="WhatsApp">
                💬
              </a>
            </span>
          </p>

          {order.observations && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-2.5 mb-3 text-xs font-body text-amber-800">
              <span className="font-bold">Observaciones:</span> {order.observations}
            </div>
          )}

          <div className="space-y-3 mb-4 border-t border-[#e5dfd3]/50 pt-3">
            <p className="font-heading text-xs font-bold text-[#46371c] mb-1">Items del Pedido:</p>
            {order.items?.map((item, i) => {
              const prod = products.find(p => p.id === item.productId);
              const nameLower = (item.name || '').toLowerCase();
              const itemImg = (item.image || prod?.image || (item.productId === 'gift-promo' || nameLower.includes('vela') || nameLower.includes('letrero') || nameLower.includes('obsequio') ? '/obsequio.webp' : 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=100')).split(',')[0];
              const complementsList = item.complements_with_prices?.length 
                ? item.complements_with_prices.map(c => c.name) 
                : (item.complements || []);

              return (
                <div key={i} className="flex gap-2 items-start text-xs font-body text-[#75664d] bg-[#fcfbf9] p-2.5 rounded-xl border border-[#e5dfd3]/70">
                  <img 
                    src={itemImg} 
                    alt="" 
                    className="w-12 h-12 object-cover rounded-lg border border-[#e5dfd3] shrink-0 mt-0.5" 
                  />
                  <div className="flex-1 space-y-0.5">
                    <p className="font-semibold text-[#46371c] text-xs">{item.name} x{item.quantity || 1}</p>
                    {item.size && <p className="text-[10px]">Tamaño: <span className="text-[#46371c] font-medium">{item.size}</span></p>}
                    {item.flavor && <p className="text-[10px]">Sabor: <span className="text-[#46371c] font-medium">{item.flavor}</span></p>}
                    {item.filling && <p className="text-[10px]">Relleno: <span className="text-[#46371c] font-medium">{item.filling}</span></p>}
                    
                    {complementsList.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {complementsList.map((comp, cIdx) => (
                          <span key={cIdx} className="inline-flex items-center text-[10px] bg-amber-100/90 text-amber-900 font-bold px-1.5 py-0.5 rounded-md border border-amber-200">
                            🎁 {typeof comp === 'string' ? comp : comp.name}
                          </span>
                        ))}
                      </div>
                    )}

                    {item.observations && <p className="text-[10px] text-[#e05275] font-semibold bg-[#e05275]/10 p-1 rounded mt-1">Especificaciones: {item.observations}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-3 pt-3 border-t border-[#e5dfd3]/60 space-y-1 text-xs font-body text-[#75664d]">
          <div className="flex justify-between">
            <span>Total:</span>
            <span className="font-semibold text-[#46371c]">{formatPrice(computedTotal)}</span>
          </div>
          {order.delivery_type === 'delivery' && (
            <div className="flex justify-between text-[#75664d]">
              <span>Costo Domicilio:</span>
              <span>{formatPrice(order.delivery_fee || 0)}</span>
            </div>
          )}
          <div className="flex justify-between text-green-700">
            <span>Abonado:</span>
            <span className="font-semibold">{formatPrice(order.amount_paid || 0)}</span>
          </div>
          <div className="flex justify-between text-red-700 font-semibold border-b border-[#e5dfd3]/30 pb-1.5 mb-1.5">
            <span>Saldo Pendiente:</span>
            <span>{formatPrice(computedBalance)}</span>
          </div>
          
          {showPaymentForm[order.id] ? (
            <div className="bg-white/90 p-2.5 rounded-xl border border-orange-200 space-y-2 mt-2">
              <p className="font-heading text-[10px] font-bold text-[#46371c]">Agregar Pago</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <label className="text-[9px] block text-[#75664d] mb-0.5">Monto (COP)</label>
                  <input
                    type="number"
                    value={paymentAmount[order.id] !== undefined ? paymentAmount[order.id] : computedBalance}
                    onChange={(e) => setPaymentAmount(prev => ({ ...prev, [order.id]: Number(e.target.value) }))}
                    className="w-full h-7 px-2 rounded-lg border border-[#e5dfd3] text-xs text-[#46371c]"
                  />
                </div>
                <div>
                  <label className="text-[9px] block text-[#75664d] mb-0.5">Fecha Pago</label>
                  <input
                    type="date"
                    value={paymentDate[order.id] || new Date().toISOString().split('T')[0]}
                    onChange={(e) => setPaymentDate(prev => ({ ...prev, [order.id]: e.target.value }))}
                    className="w-full h-7 px-2 rounded-lg border border-[#e5dfd3] text-xs text-[#46371c]"
                  />
                </div>
                <div>
                  <label className="text-[9px] block text-[#75664d] mb-0.5">Método</label>
                  <select
                    value={paymentMethod[order.id] || 'cash'}
                    onChange={(e) => setPaymentMethod(prev => ({ ...prev, [order.id]: e.target.value }))}
                    className="w-full h-7 px-1 rounded-lg border border-[#e5dfd3] text-xs text-[#46371c]"
                  >
                    <option value="cash">Efectivo</option>
                    <option value="trans_keiks">Trans Keiks</option>
                    <option value="trans_esteba">Trans Esteba</option>
                    <option value="credito">Crédito</option>
                    <option value="datafono">Datáfono</option>
                    <option value="online">Online / Pasarela</option>
                    <option value="rappi">Rappi</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-2 justify-end pt-1">
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => setShowPaymentForm(prev => ({ ...prev, [order.id]: false }))}
                  className="text-[10px] h-6"
                >
                  Cancelar
                </Button>
                <Button
                  size="xs"
                  onClick={() => handleAddPayment(order.id)}
                  className="bg-[#46371c] text-[#fcfbf9] hover:bg-[#2d2312] text-[10px] h-6 px-3 rounded-md"
                >
                  Guardar
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2 items-center justify-between pt-1">
              {isPendingPayment ? (
                user?.role !== 'domiciliario' ? (
                  <Button
                    size="xs"
                    onClick={() => setShowPaymentForm(prev => ({ ...prev, [order.id]: true }))}
                    className="bg-orange-600 text-white hover:bg-orange-700 text-[10px] h-6 px-3 rounded-full font-medium"
                  >
                    Registrar Pago
                  </Button>
                ) : (
                  <span className="text-[10px] text-amber-700 font-semibold bg-amber-100 px-2 py-0.5 rounded-full">Pago Pendiente</span>
                )
              ) : (
                <span className="text-[10px] text-green-700 font-semibold bg-green-100 px-2 py-0.5 rounded-full">Totalmente Pagado</span>
              )}
              
              <div className="flex gap-1.5 ml-auto">
                {order.status !== 'on_the_way' && order.delivery_type === 'delivery' && (
                  <Button 
                    onClick={() => markOnTheWay(order.id)} 
                    size="xs" 
                    className="bg-orange-500 hover:bg-orange-600 text-white rounded-full font-body text-[10px] h-6 px-2.5 font-semibold"
                  >
                    ⚡ En Camino
                  </Button>
                )}
                <Button 
                  onClick={() => markDelivered(order.id)} 
                  size="xs" 
                  className="bg-green-600 text-white hover:bg-green-700 rounded-full font-body text-[10px] h-6 px-2.5"
                >
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Entregado
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>
    );
  };

  return (
    <div data-testid="admin-deliveries-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#46371c]">Entregas</h1>
          <p className="font-body text-sm text-[#75664d] mt-1">Pedidos listos y despachos diarios</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/admin/reportes?type=delivered_orders">
            <Button
              variant="outline"
              size="sm"
              className="h-10 rounded-xl text-xs font-body font-semibold text-[#46371c] border-[#e5dfd3] bg-[#fcfbf9] hover:bg-[#f4f1ea] flex items-center gap-1.5 shadow-sm"
            >
              <FileText className="w-4 h-4 text-emerald-600" />
              Reporte de Entregados
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <span className="font-body text-xs text-[#75664d]">Fecha:</span>
            <input
              type="date"
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
              className="h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
            />
            {selectedDate && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedDate('')}
                className="h-8 rounded-lg text-xs font-body text-[#46371c] border-[#e5dfd3]"
              >
                Ver Todas
              </Button>
            )}
          </div>
          <Badge className="bg-orange-100 text-orange-800 text-sm py-1.5 px-3 rounded-xl font-body">
            {sortedAndFiltered.length} filtrados ({orders.length} totales)
          </Badge>
        </div>
      </div>

      {/* Summary Header Counters per Store */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <Card className="p-4 bg-amber-50/70 border border-amber-200 rounded-2xl flex items-center justify-between shadow-sm">
          <div>
            <p className="font-heading text-xs font-bold text-amber-900 uppercase tracking-wide">🏪 Reclamar en Tienda</p>
            <p className="font-heading text-2xl font-bold text-amber-900 mt-1">
              {pickupOrders.length} <span className="text-xs font-normal text-amber-800">pedidos ({activeRegion === 'Todos' ? 'Todas las sedes' : `Sede ${activeRegion}`})</span>
            </p>
          </div>
          <span className="text-3xl">🛍️</span>
        </Card>
        <Card className="p-4 bg-orange-50/70 border border-orange-200 rounded-2xl flex items-center justify-between shadow-sm">
          <div>
            <p className="font-heading text-xs font-bold text-orange-900 uppercase tracking-wide">🚚 Domicilio</p>
            <p className="font-heading text-2xl font-bold text-orange-900 mt-1">
              {deliveryOrders.length} <span className="text-xs font-normal text-orange-800">pedidos ({activeRegion === 'Todos' ? 'Todas las sedes' : `Sede ${activeRegion}`})</span>
            </p>
          </div>
          <span className="text-3xl">🛵</span>
        </Card>
      </div>

      {/* Region Tabs */}
      <div className="flex border-b border-[#e5dfd3] mb-6 overflow-x-auto pb-1 scrollbar-thin gap-1">
        {regions.map(r => (
          <button
            key={r}
            onClick={() => setActiveRegion(r)}
            className={`px-4 py-2 font-body text-sm font-semibold border-b-2 transition-all whitespace-nowrap ${
              activeRegion === r
                ? 'border-[#46371c] text-[#46371c]'
                : 'border-transparent text-[#75664d] hover:text-[#46371c]'
            }`}
          >
            {r}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#a0d5ba] border-t-transparent rounded-full animate-spin" /></div>
      ) : sortedAndFiltered.length === 0 ? (
        <Card className="p-12 text-center border border-[#e5dfd3] bg-[#fcfbf9] rounded-2xl">
          <Truck className="w-12 h-12 mx-auto text-[#e5dfd3] mb-4" />
          <p className="font-body text-[#75664d]">No hay entregas pendientes para esta fecha o región</p>
        </Card>
      ) : (
        <div className="space-y-8">
          {/* Deliveries Section */}
          {deliveryOrders.length > 0 && (
            <div>
              <h2 className="font-heading text-lg font-bold text-[#46371c] mb-4 border-b pb-2">🚚 Domicilios ({deliveryOrders.length})</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {deliveryOrders.map(renderOrderCard)}
              </div>
            </div>
          )}

          {/* Pickup Section */}
          {pickupOrders.length > 0 && (
            <div>
              <h2 className="font-heading text-lg font-bold text-[#46371c] mb-4 border-b pb-2">🏪 Retiro en Tienda ({pickupOrders.length})</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pickupOrders.map(renderOrderCard)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
